import { Fragment } from 'react';
import {
  Entity,
  RELATION_OWNED_BY,
  RELATION_PART_OF,
  stringifyEntityRef,
  SystemEntity,
  ComponentEntity,
} from '@backstage/catalog-model';
import Grid from '@mui/material/Grid';
import {
  getEntityRelations,
  EntityRefLink,
} from '@backstage/plugin-catalog-react';
import { useEntities } from '@internal/plugin-medidata-catalog-react';
import { makeStyles, createStyles } from '@mui/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import { MaturityRankWidget } from '@internal/plugin-maturity';

// This component displays all the domains with their systems and number of components in each.

type SystemWithCount = {
  entity: SystemEntity;
  count: number;
};

type CardData = {
  domain: Entity;
  owner: Entity;
  systems: SystemWithCount[];
};

const useStyles = makeStyles(() =>
  createStyles({
    header: {
      textAlign: 'center',
    },
  }),
);

const extractSystems = (allEntities: Entity[]): SystemWithCount[] => {
  const components = allEntities.filter(
    entity => entity.kind === 'Component' && entity.spec?.system !== undefined,
  );

  const componentsCountForSystem = components.reduce((group, component) => {
    const c = component as ComponentEntity;
    const system = c.spec?.system?.toLowerCase() as string;
    group[system] = (group[system] || 0) + 1;
    return group;
  }, {} as { [key: string]: number });

  const catalogSystems = allEntities.filter(entity => entity.kind === 'System');
  return catalogSystems.flatMap(system => ({
    entity: system as SystemEntity,
    count: componentsCountForSystem[system.metadata.name.toLocaleLowerCase()],
  }));
};

const extractCardContents = (
  systems: SystemWithCount[],
  allEntities: Entity[],
): CardData[] => {
  const groupByDomain = systems.reduce((group, ref) => {
    const domainName = ref.entity.spec?.domain!;
    if (group[domainName] === undefined) {
      const domainRef = getEntityRelations(ref.entity, RELATION_PART_OF).map(
        stringifyEntityRef,
      )[0];
      const domain = allEntities.filter(
        component => domainRef === stringifyEntityRef(component),
      )[0];
      const ownerRef = getEntityRelations(domain, RELATION_OWNED_BY).map(
        stringifyEntityRef,
      )[0];
      const owner = allEntities.filter(
        component => ownerRef === stringifyEntityRef(component),
      )[0];
      group[domainName] = {
        domain: domain,
        systems: [ref],
        owner: owner,
      };
    } else {
      group[domainName].systems.push(ref);
    }
    return group;
  }, {} as { [key: string]: CardData });

  return Object.values(groupByDomain);
};

export const DomainExplorerPage = () => {
  const allEntities = useEntities(['Group', 'Domain', 'System', 'Component']);
  const systems = extractSystems(allEntities);

  let cardContents = extractCardContents(systems, allEntities);
  cardContents = cardContents.filter(
    domain => !(domain.domain === undefined || domain.owner === undefined),
  );
  cardContents.sort((a, b) =>
    a.owner.metadata.name > b.owner.metadata.name ? -1 : 1,
  );

  const classes = useStyles();
  return (
    <Grid container spacing={1} columns={4}>
      {cardContents.map(entry => (
        <Grid item xs={1} key={entry.domain.metadata.name}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title={
                <EntityRefLink
                  entityRef={entry.domain}
                  defaultKind="domain"
                  title={entry.domain.metadata?.title}
                />
              }
              subheader={
                <EntityRefLink
                  entityRef={entry.owner}
                  defaultKind="group"
                  title={entry.owner.metadata?.title}
                />
              }
              action={<MaturityRankWidget entity={entry.domain} size={25} />}
              className={classes.header}
            />
            <CardContent>
              {entry.systems.map((sysCount, i) => (
                <Fragment key={sysCount.entity.metadata.name}>
                  {i > 0 && ', '}
                  <EntityRefLink
                    entityRef={sysCount.entity}
                    defaultKind="system"
                    title={sysCount.entity.metadata.title}
                  />
                  ({sysCount.count})
                </Fragment>
              ))}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};
